/**
 * Transmission Validation
 *
 * Lightweight validation helpers for transmission data.
 * Uses Zod for type-safe validation matching backend schema.
 */

import { z } from 'zod';

// ============================================================================
// Phone Number Utilities
// ============================================================================

/** Normalize phone number to digits only (for storage/validation) */
export function formatPhoneNumber(value: string): string {
  if (!value) return '';
  const hasPlus = value.startsWith('+');
  const digitsOnly = value.replace(/\D/g, '');
  return hasPlus ? `+${digitsOnly}` : digitsOnly;
}

/** Format phone number for display: (555) 123-4567 */
export function formatPhoneNumberForDisplay(value: string): string {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length > 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length > 3) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  }
  if (cleaned.length > 0) {
    return `(${cleaned}`;
  }
  return '';
}

function isValidPhoneNumber(value: string): boolean {
  if (!value) return false;
  const formatted = formatPhoneNumber(value);
  if (formatted.startsWith('+')) {
    return /^\+\d{1,3}\d{10,}$/.test(formatted);
  }
  return /^\d{10,}$/.test(formatted);
}

// ============================================================================
// Transmission Validation Schema (minimal for isValidTransmission)
// ============================================================================

const addressSchema = z.object({
  address_line1: z.string().min(1),
  address_line2: z.string().nullable().optional(),
  city: z.string().min(1),
  state: z.string().min(1),
  zip_code: z.string().min(1),
});

const faxSchema = z.object({
  type: z.literal('fax'),
  display_name: z.string().min(1),
  fax_number: z.string().refine(isValidPhoneNumber),
});

const emailSchema = z.object({
  type: z.literal('email'),
  display_name: z.string().min(1),
  email: z.string().email(),
});

const mailSchema = z.object({
  type: z.literal('mailed_letter'),
  display_name: z.string().min(1),
  address: addressSchema,
});

const chequeSchema = z.object({
  type: z.literal('mailed_cheque'),
  display_name: z.string().min(1),
  address: addressSchema,
  amount: z.string().min(1),
});

const transmissionSchema = z.discriminatedUnion('type', [
  faxSchema,
  emailSchema,
  mailSchema,
  chequeSchema,
]);

// ============================================================================
// Public API
// ============================================================================

/** Check if transmission data is valid for sending */
export function isValidTransmission(data: unknown): boolean {
  return transmissionSchema.safeParse(data).success;
}
